import React, { useState, useEffect } from 'react';
import {
    Globe,
    ExternalLink,
    Save,
    Plus,
    Trash2,
    Clock,
    DollarSign,
    ChevronRight,
    Eye,
    Settings as SettingsIcon,
    Palette,
    Layout,
    MessageSquare,
    Zap,
    Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../lib/api';

const BookingPageCreator = () => {
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pageId, setPageId] = useState(null);

    // Page State
    const [clinicName, setClinicName] = useState('Minha Clínica');
    const [description, setDescription] = useState('Especialistas no cuidado da sua saúde.');
    const [slug, setSlug] = useState('minha-clinica');
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');

    // Services State
    const [services, setServices] = useState([]);

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await api.get('/api/booking/config');
            if (response.ok) {
                const data = await response.json();
                if (data.page) {
                    setPageId(data.page.id);
                    setClinicName(data.page.clinic_name);
                    setDescription(data.page.description || '');
                    setSlug(data.page.slug);
                    setAddress(data.page.address || '');
                    setPhone(data.page.contact_phone || '');
                }
                if (data.services && data.services.length > 0) {
                    setServices(data.services);
                } else {
                    // Default services if none exist
                    setServices([
                        { id: 'temp-1', name: 'Consulta Geral', price: '150', duration: '30 min', description: 'Atendimento médico primário.' }
                    ]);
                }
            }
        } catch (error) {
            console.error("Error fetching booking config:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                page: {
                    id: pageId,
                    clinic_name: clinicName,
                    description: description,
                    slug: slug,
                    address: address,
                    contact_phone: phone
                },
                services: services.map(s => ({
                    name: s.name,
                    price: parseFloat(s.price) || 0,
                    duration: s.duration,
                    description: s.description,
                    is_active: true
                }))
            };

            const response = await api.post('/api/booking/config', payload);

            if (response.ok) {
                const result = await response.json();
                setPageId(result.page_id);
                alert(t('booking.savedSuccess'));
            } else {
                alert(t('booking.saveError'));
            }
        } catch (error) {
            console.error("Error saving config:", error);
            alert(t('booking.connectionError'));
        } finally {
            setSaving(false);
        }
    };

    const addService = () => {
        const newId = `temp-${Date.now()}`;
        setServices([...services, { id: newId, name: '', price: '', duration: '', description: '' }]);
    };

    const updateService = (id, field, value) => {
        setServices(services.map(s => s.id === id ? { ...s, [field]: value } : s));
    };

    const removeService = (id) => {
        setServices(services.filter(s => s.id !== id));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-8 max-w-6xl mx-auto min-h-screen">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-[#0f172a] flex items-center gap-2">
                        <Globe className="h-8 w-8 text-blue-600" />
                        {t('booking.title')}
                    </h1>
                    <p className="text-slate-500 mt-1">{t('booking.subtitle')}</p>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="outline"
                        className="flex items-center gap-2"
                        onClick={() => window.open(`/reserve/${slug}`, '_blank')}
                    >
                        <Eye className="h-4 w-4" />
                        {t('common.preview')}
                    </Button>
                    <Button
                        disabled={saving}
                        onClick={handleSave}
                        className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2 min-w-[140px]"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {saving ? t('common.saving') : t('common.saveChanges')}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Sidebar Navigation */}
                <div className="lg:col-span-1 space-y-1">
                    <button
                        onClick={() => setActiveTab('general')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === 'general' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <SettingsIcon className="h-4 w-4" />
                        {t('booking.tabGeneral')}
                    </button>
                    <button
                        onClick={() => setActiveTab('services')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === 'services' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Layout className="h-4 w-4" />
                        {t('booking.tabServices')}
                    </button>
                    <button
                        onClick={() => setActiveTab('appearance')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === 'appearance' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Palette className="h-4 w-4" />
                        {t('booking.tabAppearance')}
                    </button>
                    <button
                        onClick={() => setActiveTab('settings')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === 'settings' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <MessageSquare className="h-4 w-4" />
                        {t('booking.tabBotIntegration')}
                    </button>
                </div>

                {/* Content Area */}
                <div className="lg:col-span-3 space-y-6">
                    {activeTab === 'general' && (
                        <Card className="border-slate-200 shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-xl">{t('booking.generalInfo')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-slate-700">{t('booking.clinicName')}</label>
                                        <Input
                                            value={clinicName}
                                            onChange={(e) => setClinicName(e.target.value)}
                                            placeholder={t('booking.clinicNamePlaceholder')}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-slate-700">{t('booking.slug')}</label>
                                        <Input
                                            value={slug}
                                            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                                            placeholder={t('booking.slugPlaceholder')}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">{t('booking.shortDescription')}</label>
                                    <textarea
                                        className="w-full min-h-[80px] border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={t('booking.shortDescriptionPlaceholder')}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-slate-700">{t('booking.contactPhone')}</label>
                                        <Input
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder={t('booking.contactPhonePlaceholder')}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-slate-700">{t('booking.address')}</label>
                                        <Input
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            placeholder={t('booking.addressPlaceholder')}
                                        />
                                    </div>
                                </div>
                                <div className="pt-4 border-t border-slate-100">
                                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
                                        <div>
                                            <p className="font-semibold text-[#0f172a]">{t('booking.pageUrl')}</p>
                                            <p className="text-sm text-slate-500">https://chatalfa.com/reserve/{slug}</p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="gap-2"
                                            onClick={() => window.open(`/reserve/${slug}`, '_blank')}
                                        >
                                            <ExternalLink className="h-4 w-4" />
                                            {t('booking.visit')}
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 'services' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-[#0f172a]">{t('booking.yourServices')}</h3>
                                <Button onClick={addService} variant="outline" className="gap-2 border-blue-200 text-blue-600 hover:bg-blue-50">
                                    <Plus className="h-4 w-4" />
                                    {t('booking.addService')}
                                </Button>
                            </div>

                            {services.map((service) => (
                                <Card key={service.id} className="border-slate-200 shadow-sm overflow-hidden group">
                                    <div className="p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('booking.serviceName')}</label>
                                                <Input
                                                    value={service.name}
                                                    onChange={(e) => updateService(service.id, 'name', e.target.value)}
                                                    placeholder={t('booking.serviceNamePlaceholder')}
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('booking.price')}</label>
                                                    <div className="relative">
                                                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                        <Input
                                                            className="pl-9"
                                                            value={service.price}
                                                            onChange={(e) => updateService(service.id, 'price', e.target.value)}
                                                            placeholder="0,00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('booking.duration')}</label>
                                                    <div className="relative">
                                                        <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                        <Input
                                                            className="pl-9"
                                                            value={service.duration}
                                                            onChange={(e) => updateService(service.id, 'duration', e.target.value)}
                                                            placeholder="30 min"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="md:col-span-2 space-y-2">
                                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('booking.serviceDescription')}</label>
                                                <Input
                                                    value={service.description}
                                                    onChange={(e) => updateService(service.id, 'description', e.target.value)}
                                                    placeholder={t('booking.serviceDescriptionPlaceholder')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex justify-end">
                                        <Button variant="ghost" onClick={() => removeService(service.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 px-2">
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            {t('common.delete')}
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <Card className="border-slate-200 shadow-sm">
                            <CardHeader>
                                <CardTitle className="text-xl">{t('booking.botIntegration')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
                                    <div className="flex items-center gap-4">
                                        <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center text-white">
                                            <Zap className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-blue-900">{t('booking.autoBookingAI')}</p>
                                            <p className="text-sm text-blue-700">{t('booking.autoBookingDesc')}</p>
                                        </div>
                                    </div>
                                    <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
                                        <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-semibold text-slate-800">{t('booking.bookingFlow')}</h4>
                                    <div className="p-4 border border-slate-200 rounded-xl flex items-center justify-between hover:border-blue-200 hover:bg-slate-50 cursor-pointer transition-all">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 bg-slate-100 rounded flex items-center justify-center text-slate-500">1</div>
                                            <span className="text-sm font-medium">{t('booking.flowStep1')}</span>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-slate-400" />
                                    </div>
                                    <div className="p-4 border border-slate-200 rounded-xl flex items-center justify-between hover:border-blue-200 hover:bg-slate-50 cursor-pointer transition-all">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 bg-slate-100 rounded flex items-center justify-center text-slate-500">2</div>
                                            <span className="text-sm font-medium">{t('booking.flowStep2')}</span>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-slate-400" />
                                    </div>
                                    <div className="p-4 border border-slate-200 rounded-xl flex items-center justify-between hover:border-blue-200 hover:bg-slate-50 cursor-pointer transition-all">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 bg-slate-100 rounded flex items-center justify-center text-slate-500">3</div>
                                            <span className="text-sm font-medium">{t('booking.flowStep3')}</span>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-slate-400" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookingPageCreator;
